import Image from 'components/Common/Image'
import Pane from 'components/Common/Pane'
import { isExternalUrl, removeRegionFromPath } from 'lib/url/searchUrlUtils'
import { LoyaltyOffersHubOfferTag, LoyaltyOffersHubUrgencyType } from 'loyalty/lib/loyaltyOffersHub/constants'
import type { LoyaltyOffersHubOffer } from 'loyalty/lib/loyaltyOffersHub/types'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import styled from 'styled-components'
import LoyaltyOffersHubOfferLabel from './LoyaltyOffersHubOfferLabel'

const OfferCard = styled(Pane)`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  height: 100%;
  border: none;
`

const OfferImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${rem(180)};
  overflow: hidden;
`

const OfferImage = styled(Image)`
  display: block;
  width: 100%;
  height: 100%;
`

const OfferLabel = styled.div`
  display: flex;
`

const OfferContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${rem(8)};
`

const CtaContainer = styled.div`
  margin-top: auto;
`

interface Props {
  offer: LoyaltyOffersHubOffer
}

function LoyaltyOffersHubOfferCard({ offer }: Props) {
  const isCtaExternalLink = isExternalUrl(offer.ctaUrl)
  const ctaLink = <TextLink
    size="medium"
    weight="regular"
    to={isCtaExternalLink ? undefined : removeRegionFromPath(offer.ctaUrl)}
    href={isCtaExternalLink ? offer.ctaUrl : undefined}
    target={isCtaExternalLink ? '_blank' : undefined}
    rel={isCtaExternalLink ? 'noopener noreferrer' : undefined}
  >
    {offer.ctaLabel}
  </TextLink>
  const hasOfferLabel = offer.urgencyType !== LoyaltyOffersHubUrgencyType.NONE
  const label = hasOfferLabel && <OfferLabel>
    <LoyaltyOffersHubOfferLabel offer={offer} />
  </OfferLabel>

  let content: ReactNode

  if (offer.offerTag === LoyaltyOffersHubOfferTag.PARTNER_OFFER) {
    content = <>
      {!!offer.brandName && <BodyTextBlock variant="large" colour="neutral-one">
        {offer.brandName}
      </BodyTextBlock>}
      <Heading variant="heading4">{offer.title}</Heading>
      {label}
      <BodyText variant="small" colour="light-foreground-alt">{offer.description}</BodyText>
      <CtaContainer>{ctaLink}</CtaContainer>
    </>
  } else {
    content = <>
      {label}
      <Heading variant="heading4">{offer.title}</Heading>
      <BodyText variant="small" colour="light-foreground-alt">{offer.description}</BodyText>
      <CtaContainer>{ctaLink}</CtaContainer>
    </>
  }

  return <OfferCard borderRadius="L">
    <OfferImageContainer>
      <OfferImage id={offer.imageId} fit="center" objectFit="cover" />
    </OfferImageContainer>
    <OfferContent>
      {content}
    </OfferContent>
  </OfferCard>
}

export default LoyaltyOffersHubOfferCard
